// Shared landing page styles - extracted from AgentLiteLanding
// Use these across all marketing pages for consistency

export const LANDING_COLORS = {
  primary: '#7c3aed',           // Purple - Brand primary
  primaryLight: '#8b5cf6',      // Lighter purple variant
  accent: '#10b981',            // Emerald green - Accent color

  whatsapp: {
    green: '#25D366',           // WhatsApp brand green
    dark: '#128C7E',            // WhatsApp darker green
  },

  google: {
    blue: '#4285F4',            // Google's official blue
    red: '#EA4335',             // Google's official red
    yellow: '#FBBC05',          // Google's official yellow
    green: '#34A853',           // Google's official green
  },

  text: {
    primary: '#111827',         // Dark gray - Main text
    secondary: '#4b5563',       // Medium gray - Secondary text
    muted: '#6b7280',           // Light gray - Tertiary text
  },

  border: '#e5e7eb',            // Light border color

  background: {
    white: '#ffffff',
    muted: '#f9fafb',           // Light gray background
  },
};

export const LANDING_GRADIENTS = {
  // Hero section gradient - purple to green to cyan
  hero: 'linear-gradient(135deg, #faf5ff 0%, #f0fdf4 40%, #ecfeff 100%)',

  // Section background gradient
  section: 'linear-gradient(180deg, #faf5ff 0%, #f0fdf4 100%)',

  // Alternative section gradient (reversed)
  sectionAlt: 'linear-gradient(180deg, #ffffff 0%, #faf5ff 50%, #f0fdf4 100%)',

  // CTA button gradient (WhatsApp green)
  cta: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',

  // Decorative ring gradient
  decorativeRing: 'linear-gradient(135deg, #25D366 0%, #7c3aed 100%)',

  // Text gradient (for hero headlines)
  text: 'linear-gradient(135deg, #7c3aed 0%, #25D366 50%, #7c3aed 100%)',
};

export const LANDING_SHADOWS = {
  // Card shadows
  card: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
  cardHover: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
  cardLarge: '0 25px 50px -12px rgba(124, 58, 237, 0.15)',

  // Hero image shadow
  hero: '0 25px 80px -20px rgba(124, 58, 237, 0.25), 0 10px 30px -10px rgba(0,0,0,0.1)',

  // CTA button glow
  ctaGlow: '0 4px 20px rgba(37, 211, 102, 0.3)',
  ctaGlowHover: '0 4px 30px rgba(37, 211, 102, 0.5)',
};

// CSS-in-JS animations (for inline styles)
export const LANDING_ANIMATIONS = {
  float: 'float 8s ease-in-out infinite',
  floatReverse: 'float 10s ease-in-out infinite reverse',
  floatSlow: 'float 12s ease-in-out infinite',
  pulseGlow: 'pulse-glow 2s infinite',
};

// Keyframe definitions (inject into page via style tag)
export const LANDING_KEYFRAMES = `
  @keyframes float {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-20px) rotate(5deg); }
  }

  @keyframes pulse-glow {
    0%, 100% {
      box-shadow: 0 4px 20px rgba(37, 211, 102, 0.3), 0 0 0 0 rgba(37, 211, 102, 0.4);
    }
    50% {
      box-shadow: 0 4px 30px rgba(37, 211, 102, 0.5), 0 0 0 8px rgba(37, 211, 102, 0);
    }
  }

  @keyframes shimmer {
    0% { background-position: -200% center; }
    100% { background-position: 200% center; }
  }

  @keyframes pulse-ring {
    0% { transform: scale(1); opacity: 0.8; }
    100% { transform: scale(1.3); opacity: 0; }
  }
`;

// Utility: Get icon background color with opacity
export const getIconBgColor = (color: string, opacity: number = 0.1): string => {
  // Convert hex to rgb and apply opacity
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

// Common style patterns as objects (for inline styles)
export const LANDING_STYLE_PATTERNS = {
  // Hero gradient text
  gradientText: {
    background: LANDING_GRADIENTS.text,
    backgroundSize: '200% auto',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  } as React.CSSProperties,

  // CTA button with glow
  ctaButton: {
    background: LANDING_GRADIENTS.cta,
  } as React.CSSProperties,

  // Hero background
  heroBackground: {
    background: LANDING_GRADIENTS.hero,
  } as React.CSSProperties,
};
